/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.feature.rates.navigation

import androidx.navigation.NavController
import androidx.navigation.NavGraphBuilder
import androidx.navigation.NavOptions
import androidx.navigation.navigation
import androidx.navigation.toRoute
import kotlinx.serialization.Serializable
import kpt.core.base.ui.nav.FeatureDestination
import kpt.core.base.ui.nav.composableWithPushTransitions
import kpt.core.base.ui.nav.popBackStackSafely
import kpt.feature.rates.ui.InterestRateDetailScreen
import kpt.feature.rates.ui.InterestRatesScreen

/** Parent graph route for the B7 Interest Rate Tracker. */
@Serializable
data object RatesGraphRoute

/** Default destination — the multi-series dashboard. */
@Serializable
data object RatesListRoute

/**
 * Per-series detail destination.
 *
 * @property seriesId FRED series id, e.g. `"DFF"`.
 */
@Serializable
data class RateDetailRoute(val seriesId: String)

/**
 * Navigates to the interest-rates dashboard.
 *
 * @param navOptions optional nav options.
 */
fun NavController.navigateToRates(navOptions: NavOptions? = null) {
    navigate(route = RatesGraphRoute, navOptions = navOptions)
}

/**
 * Navigates to one series' detail.
 *
 * @param seriesId the FRED series id.
 * @param navOptions optional nav options.
 */
fun NavController.navigateToRateDetail(seriesId: String, navOptions: NavOptions? = null) {
    navigate(route = RateDetailRoute(seriesId = seriesId), navOptions = navOptions)
}

/**
 * Add the B7 Interest Rate Tracker nav graph to the host `NavGraphBuilder`.
 *
 * Host apps wire this from their root navigation by calling `ratesGraph(navController)`.
 */
@FeatureDestination
fun NavGraphBuilder.ratesGraph(navController: NavController) {
    navigation<RatesGraphRoute>(startDestination = RatesListRoute) {
        composableWithPushTransitions<RatesListRoute> {
            InterestRatesScreen(
                onBackClick = { navController.popBackStackSafely() },
                onSeriesClick = { seriesId ->
                    navController.navigateToRateDetail(seriesId = seriesId)
                },
            )
        }
        composableWithPushTransitions<RateDetailRoute> { backStackEntry ->
            val route = backStackEntry.toRoute<RateDetailRoute>()
            InterestRateDetailScreen(
                seriesId = route.seriesId,
                onBackClick = { navController.popBackStackSafely() },
            )
        }
    }
}
